import { useEffect, useState } from 'react';
import { useDotBotsAuth } from '@dotbots-boutique/auth-sdk/react';
import { useApi } from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { contentToPlainText, formatContent } from '../lib/formatContent';
import { t, tp } from '../lib/i18n';
import type { Campaign } from '../lib/types';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const PLATFORMS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'discord', label: 'Discord' },
  { value: 'medium', label: 'Medium' },
];

type Status = 'pending' | 'generating' | 'done' | 'error';

interface PlatformResult {
  platform: string;
  status: Status;
  text: string;
  content?: Record<string, unknown>;
  postId?: string;
  costTokens?: string; // DotBots tokens debited from the org balance
  costEur?: number;
  inputTokens?: number; // AI input tokens consumed by the LLM
  outputTokens?: number;
  error?: string;
  code?: string;
  detail?: string;
  step?: string;
}

// 1 EUR = 1,000,000,000 DotBots tokens. Used as a fallback when the proxy
// doesn't include cost.eur in the response (older proxy versions).
function dotbotsTokensToEur(tokens: string | number): number {
  const n = typeof tokens === 'string' ? Number(tokens) : tokens;
  return n / 1_000_000_000;
}

function formatTokenCount(n: number | string | undefined): string {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function explainError(p: PlatformResult): string {
  // The proxy may return generic "Not found" when the feature code isn't
  // configured under Payments → Features (the dotbots.boutique.json manifest
  // alone is not enough — a price must be set in the platform admin).
  if (
    p.code === 'AI_FEATURE_NOT_FOUND' ||
    p.code === 'FEATURE_NOT_FOUND' ||
    p.code === 'Not found' ||
    (p.step === 'charge' && p.code === 'CHARGE_FAILED')
  ) {
    return t('contentError.featureNotFound');
  }
  if (p.code === 'AI_INSUFFICIENT_BALANCE' || p.code === 'INSUFFICIENT_BALANCE') {
    return t('contentError.insufficientBalance');
  }
  if (p.code === 'AI_PROVIDER_NOT_CONFIGURED') {
    return t('contentError.providerNotConfigured');
  }
  if (p.code === 'AI_CALL_TIMEOUT' || p.code === 'AI_CALL_FAILED') {
    return t('contentError.timeout');
  }
  if (p.code === 'BUDGET_EXCEEDED') {
    return t('contentError.budgetExceeded');
  }
  return p.detail ?? t('contentError.generic');
}

// Read the NDJSON event stream and dispatch each event to a callback. Each
// line is a JSON object: {type:'start'|'delta'|'done'|'error'|'all_done'|'fatal', ...}.
async function readEventStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: Record<string, unknown>) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      if (buffer.trim()) {
        try {
          onEvent(JSON.parse(buffer));
        } catch {
          /* ignore trailing garbage */
        }
      }
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line) continue;
      try {
        onEvent(JSON.parse(line));
      } catch {
        // Malformed line — skip
      }
    }
  }
}

export default function ContentGenerator() {
  const api = useApi();
  const { fetch: authFetch } = useDotBotsAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [selected, setSelected] = useState<string[]>(['linkedin', 'twitter']);
  const [language, setLanguage] = useState('nl');
  const [tone, setTone] = useState('informeel');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<PlatformResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // Per-platform feedback the user can enter after a generation. The next
  // regenerate call passes this as extraInstructions, prepending it to the
  // platform-specific prompt so the AI knows what to change.
  const [feedback, setFeedback] = useState<Record<string, string>>({});

  useEffect(() => {
    api<{ campaigns: Campaign[] }>('/api/campaigns')
      .then((r) => {
        setCampaigns(r.campaigns);
        if (r.campaigns.length > 0) setCampaignId(r.campaigns[0].id);
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function togglePlatform(p: string) {
    setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  }

  function updatePlatform(
    platform: string,
    patch: Partial<PlatformResult> | ((prev: PlatformResult) => Partial<PlatformResult>),
  ) {
    setResults((prev) =>
      prev.map((p) => {
        if (p.platform !== platform) return p;
        const next = typeof patch === 'function' ? patch(p) : patch;
        return { ...p, ...next };
      }),
    );
  }

  function handleEvent(event: Record<string, unknown>) {
    const type = event.type as string;
    const platform = event.platform as string | undefined;
    if (type === 'start' && platform) {
      updatePlatform(platform, { status: 'generating', text: '' });
    } else if (type === 'delta' && platform) {
      updatePlatform(platform, (prev) => ({ text: prev.text + (event.text as string) }));
    } else if (type === 'done' && platform) {
      updatePlatform(platform, {
        status: 'done',
        postId: event.postId as string,
        content: event.content as Record<string, unknown>,
        costTokens: event.costTokens as string | undefined,
        costEur: event.costEur as number | undefined,
        inputTokens: event.inputTokens as number | undefined,
        outputTokens: event.outputTokens as number | undefined,
      });
    } else if (type === 'error' && platform) {
      updatePlatform(platform, {
        status: 'error',
        error: (event.step as string) ?? 'failed',
        step: event.step as string | undefined,
        code: event.code as string | undefined,
        detail: event.detail as string | undefined,
      });
    } else if (type === 'fatal') {
      setError((event.detail as string) ?? 'Streaming generation failed');
    }
  }

  async function generate() {
    setBusy(true);
    setError(null);
    setResults(
      selected.map((p) => ({ platform: p, status: 'pending' as Status, text: '' })),
    );

    try {
      const response = await authFetch(`${BACKEND_URL}/api/content/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, platforms: selected, language, tone }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `http_${response.status}`);
      }
      if (!response.body) throw new Error('no_response_body');

      await readEventStream(response.body, handleEvent);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function regenerate(platform: string, postId: string) {
    setBusy(true);
    const extraInstructions = feedback[platform]?.trim() || undefined;
    updatePlatform(platform, {
      status: 'generating',
      text: '',
      content: undefined,
      error: undefined,
      code: undefined,
      detail: undefined,
    });

    try {
      const response = await authFetch(`${BACKEND_URL}/api/content/regenerate/${postId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tone, language, extraInstructions }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `http_${response.status}`);
      }
      if (!response.body) throw new Error('no_response_body');

      await readEventStream(response.body, handleEvent);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copy(key: string, value: string) {
    await copyToClipboard(value);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
  }

  if (campaigns.length === 0) {
    return (
      <div>
        <h1 style={{ marginTop: 0 }}>{t('content.title')}</h1>
        <div className="card muted">{t('content.empty')}</div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>{t('content.title')}</h1>

      <div className="notice" style={{ marginBottom: '1rem' }}>
        {t('content.notice')}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="grid-2">
          <div>
            <label>{t('content.campaign')}</label>
            <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>{t('content.language')}</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          <div>
            <label>{t('content.tone')}</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)}>
              <option value="informeel">{t('tone.informal')}</option>
              <option value="formeel">{t('tone.formal')}</option>
              <option value="enthousiast">{t('tone.enthusiastic')}</option>
              <option value="zakelijk">{t('tone.business')}</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label>{t('content.platforms')}</label>
          <div className="row">
            {PLATFORMS.map((p) => (
              <button
                key={p.value}
                className={selected.includes(p.value) ? '' : 'secondary'}
                onClick={() => togglePlatform(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <button disabled={busy || !selected.length} onClick={generate}>
            {busy ? t('content.generating') : tp('content.generate', { n: selected.length })}
          </button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="grid-2">
          {results.map((r) => (
            <div className="card" key={r.platform}>
              <div className="flex-between">
                <h3 style={{ margin: 0, textTransform: 'capitalize' }}>{r.platform}</h3>
                <StatusBadge status={r.status} />
              </div>

              {r.status === 'error' && (
                <div className="error" style={{ marginTop: '0.5rem' }}>
                  <strong>{r.error}{r.code ? ` (${r.code})` : ''}</strong>
                  <div className="small" style={{ marginTop: '0.25rem' }}>
                    {explainError(r)}
                  </div>
                </div>
              )}

              {(r.status === 'generating' || r.status === 'done') && (
                <div
                  style={{
                    background: 'var(--surface-2)',
                    padding: '0.75rem',
                    borderRadius: 'var(--radius)',
                    fontSize: '13px',
                    marginTop: '0.5rem',
                    minHeight: '60px',
                  }}
                >
                  {r.status === 'done' && r.content
                    ? formatContent(r.platform, r.content)
                    : r.text
                      ? <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{r.text}</pre>
                      : <span className="muted">…</span>}
                </div>
              )}

              {r.status === 'done' && r.costTokens && (
                <div className="muted small" style={{ marginTop: '0.5rem' }}>
                  <div>
                    {tp('content.costEur', {
                      eur: (r.costEur ?? dotbotsTokensToEur(r.costTokens)).toFixed(6),
                    })}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>
                    {tp('content.costBreakdown', {
                      dotbots: formatTokenCount(r.costTokens),
                      input: formatTokenCount(r.inputTokens),
                      output: formatTokenCount(r.outputTokens),
                    })}
                  </div>
                </div>
              )}

              {r.status === 'done' && r.content && (
                <div className="row" style={{ marginTop: '0.5rem' }}>
                  <button
                    className="secondary"
                    onClick={() =>
                      copy(
                        `p-${r.platform}`,
                        contentToPlainText(r.platform, r.content!),
                      )
                    }
                  >
                    {copied === `p-${r.platform}` ? t('content.copied') : t('content.copyText')}
                  </button>
                </div>
              )}

              {r.status === 'done' && r.postId && (
                <div
                  style={{
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  <label style={{ fontWeight: 600 }}>
                    {t('content.feedbackLabel')}
                  </label>
                  <textarea
                    rows={2}
                    value={feedback[r.platform] ?? ''}
                    onChange={(e) =>
                      setFeedback((prev) => ({ ...prev, [r.platform]: e.target.value }))
                    }
                    placeholder={t('content.feedbackPlaceholder')}
                    style={{ marginTop: '0.25rem' }}
                  />
                  <div className="small muted" style={{ marginTop: '0.25rem' }}>
                    {t('content.feedbackHint')}
                  </div>
                  <button
                    style={{ marginTop: '0.5rem' }}
                    disabled={busy}
                    onClick={() => regenerate(r.platform, r.postId!)}
                  >
                    {feedback[r.platform]?.trim()
                      ? t('content.regenerateWithFeedback')
                      : t('content.regenerate')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const map: Record<Status, { key: string; cls: string }> = {
    pending: { key: 'contentStatus.pending', cls: '' },
    generating: { key: 'contentStatus.generating', cls: 'paused' },
    done: { key: 'contentStatus.done', cls: 'active' },
    error: { key: 'contentStatus.error', cls: 'ended' },
  };
  const { key, cls } = map[status];
  return <span className={`badge ${cls}`}>{t(key)}</span>;
}
